/**
 * অবস্থা ৪ · পেমেন্ট ব্যর্থ — `P0 States § ৫` (Failure · Payment). `E_PAYMENT_FAILED`.
 *
 * THE ONE RULE OF THIS SCREEN, from § টাকা ও সংযোগের ব্যর্থতা:
 * "টাকা জড়িত থাকলে প্রথম বাক্যেই টাকার কী হলো তা লিখতে হবে।" The design's own note
 * puts it in English: "'আপনার টাকা কাটা হয়নি' is the second line, in green, before any
 * explanation."
 *
 * So the order is fixed and this component cannot render it any other way:
 *
 *   1  PAYMENT_FAILED.title_bn   600 18px/1.75  ink        পেমেন্ট হয়নি
 *   2  moneyPositionBn          600 15.5/1.75  #1E6B4A    (the server's sentence)
 *   3  causeBn                  400 14.5/1.75  ink2       (the server's sentence)
 *
 * There is no prop that reorders the three. A version of this screen that explains
 * before it says what happened to the money is unrepresentable, which is the point.
 *
 * W1-04 · LINE 2 IS THE SERVER'S NOW, OR IT IS NOT DRAWN.
 * The drawn "আপনার টাকা কাটা হয়নি।" was printed over every payment failure, and it is a
 * claim about money this component cannot know: a transaction id claimed twice is money
 * that DID move. A frame whose only rule is "say what happened to the money first" must
 * not say the wrong thing first. `ApiErrorBody.money_position_bn` carries it when the
 * server knows it; absent, line 2 is absent and nothing stands in its place.
 *
 * The rest of what went (`docs/deviations/W1-04.md`, S5): the drawn cause (a bank
 * refusing a card — no card rail is offered), the order block (an order nobody placed),
 * and the three drawn ways forward — অপ্রাপ্য ক্যাশ, withdrawn as a payment method with
 * an invented amount beneath it; a card retry; and হাতে হাতে, which is the order's fact.
 * The way forward that is always true stays: try again. The reference in the footer is
 * the error body's own, and absent when it has none.
 *
 * DEVIATION, reported: `@oprappo/ui`'s `FailureState.Money` encodes the same rule with
 * the same required second line, but § 07 draws it on `#FAF2F1` with a
 * `border-top:3px` danger rule, an `#8E2018` title and a Tiro Bangla cause. § ৫ draws
 * a WHITE card with `border:1px solid #B3271E` and a `border-left:3px` danger rule, an
 * ink title and an Anek Bangla cause. Two drawings of one rule; the frame is
 * transcribed and the component is not forked.
 *
 * The reference code sits in the footer, not the headline — "no error codes in the
 * primary line" (§ যা এখানে নেই).
 */
import { ariaLabel } from '@oprappo/tokens';
import { StateFrame, RowChevron, SectionLabel, BackChevron } from './chrome.js';
import { PAYMENT_FAILED } from './data.js';

export interface PaymentFailedStateProps {
  /**
   * `error.money_position_bn` — what happened to the money, in the server's words. The
   * second line, before any explanation. Absent or empty: not drawn.
   */
  moneyPositionBn?: string | undefined;
  /** `error.message_bn` — why. Absent or empty: not drawn. */
  causeBn?: string | undefined;
  /** `error.reference` — the string a user reads aloud to support. Absent or empty: not drawn. */
  reference?: string | undefined;
  onRetry?: (() => void) | undefined;
  onBack?: (() => void) | undefined;
}

/** A server sentence that is there to show. `''` is the server saying it has none. */
function said(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

export function PaymentFailedState({
  moneyPositionBn,
  causeBn,
  reference,
  onRetry,
  onBack,
}: PaymentFailedStateProps = {}): JSX.Element {
  const d = PAYMENT_FAILED;
  return (
    <StateFrame stateKey="payment_failed">
      <div className="bg-white border-b border-border px-[16px] py-[14px] flex gap-[12px] items-center">
        <button
          type="button"
          aria-label={ariaLabel.back}
          onClick={onBack}
          className="bg-transparent border-0 p-0 rounded-none flex-none"
        >
          <BackChevron />
        </button>
        <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{d.header_bn}</div>
      </div>

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div role="alert" className="bg-white border border-danger border-l-[3px] border-l-danger p-[15px]">
          <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{d.title_bn}</div>
          {/* SECOND. In green. Before any explanation. Do not move this line. */}
          {said(moneyPositionBn) ? (
            <div className="font-anek font-semibold text-[15.5px] leading-[1.75] text-success mt-[9px]">
              {moneyPositionBn}
            </div>
          ) : null}
          {said(causeBn) ? (
            <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[7px]">
              {causeBn}
            </div>
          ) : null}
        </div>

        <SectionLabel className="mt-[20px] mb-[11px]">{d.next_label_bn}</SectionLabel>
        <button
          type="button"
          onClick={onRetry}
          className="w-full bg-white border-[1.5px] border-teal p-[15px] flex justify-between items-center gap-[11px] text-left rounded-none"
        >
          <span className="flex-1 font-anek font-semibold text-[15.5px] leading-[1.75] text-teal">
            {d.retry_bn}
          </span>
          <RowChevron size={16} stroke="#0E3A45" />
        </button>
      </div>

      <div className="bg-sand border-t border-border px-[16px] py-[12px] flex justify-between items-center gap-[11px]">
        <div className="font-anek font-normal text-[13px] leading-[1.75] text-muted">{d.support_bn}</div>
        {/* Latin, Manrope, `500 13px/1.5` — a reference, not a headline. */}
        {said(reference) ? (
          <div className="font-manrope font-medium text-[13px] leading-[1.5] text-muted flex-none">
            {reference}
          </div>
        ) : null}
      </div>
    </StateFrame>
  );
}
